import React from "react";
import { Link } from "react-router-dom";
import { Sparkles, Clock, AlertTriangle } from "lucide-react";
import { useSubscription, daysUntil } from "@/context/SubscriptionContext";

/**
 * Persistent banner shown inside AppShell:
 *  - Free users → soft upsell (Start free trial)
 *  - Trialing → countdown chip
 *  - Trial expired / past due → firm CTA to upgrade
 *  - Active + cancel_at_period_end → renewal reminder
 * Admins never see this.
 */
export default function TrialBanner() {
  const { subscription, effective_status, eligible_plan, loading } = useSubscription();
  if (loading) return null;
  if (!subscription) return null;

  const status = effective_status;
  const plan = eligible_plan;

  if (status === "free") {
    return (
      <Banner tone="primary" testid="trial-banner-free" icon={Sparkles}>
        <div className="text-sm">
          <strong>Try {plan?.name || "Pro"} free for {plan?.trial_days || 14} days.</strong>
          <span className="text-primary-foreground/80"> Unlock analytics, verified badge and priority support.</span>
        </div>
        <Link to="/upgrade" className="rounded-md bg-white text-primary text-sm font-medium px-4 py-2 whitespace-nowrap" data-testid="trial-banner-upgrade-cta">
          Start free trial →
        </Link>
      </Banner>
    );
  }

  if (status === "trialing") {
    const left = daysUntil(subscription.trial_ends_at);
    return (
      <Banner tone="accent" testid="trial-banner-active" icon={Clock}>
        <div className="text-sm">
          <strong>Trial: {left} day{left === 1 ? "" : "s"} left</strong>
          <span className="text-primary/70"> — add a payment method to keep {plan?.name || "Pro"} active.</span>
        </div>
        <Link to="/upgrade" className="rounded-md bg-primary text-primary-foreground text-sm font-medium px-4 py-2 whitespace-nowrap" data-testid="trial-banner-add-payment">
          Upgrade to Pro
        </Link>
      </Banner>
    );
  }

  if (status === "trial_expired" || status === "past_due") {
    return (
      <Banner tone="destructive" testid="trial-banner-expired" icon={AlertTriangle}>
        <div className="text-sm">
          <strong>Your {plan?.name || "Pro"} access has ended.</strong>
          <span className="opacity-90"> Pro features are locked until you subscribe.</span>
        </div>
        <Link to="/upgrade" className="rounded-md bg-white text-destructive text-sm font-medium px-4 py-2 whitespace-nowrap" data-testid="trial-banner-renew">
          Renew Pro
        </Link>
      </Banner>
    );
  }

  if (status === "active" && subscription.cancel_at_period_end) {
    return (
      <Banner tone="muted" testid="trial-banner-cancel" icon={Clock}>
        <div className="text-sm text-primary">
          Your plan will not renew after <strong>{formatDate(subscription.current_period_end)}</strong>.
        </div>
        <Link to="/billing" className="text-sm text-primary underline" data-testid="trial-banner-manage">Manage billing</Link>
      </Banner>
    );
  }

  return null;
}

function Banner({ tone, testid, icon: Icon, children }) {
  const toneClasses = {
    primary: "bg-primary text-primary-foreground border-primary",
    accent: "bg-accent/12 text-primary border-accent/40",
    destructive: "bg-destructive text-destructive-foreground border-destructive",
    muted: "bg-muted text-primary border-border",
  }[tone];
  return (
    <div
      className={`rounded-md border ${toneClasses} px-4 py-3 flex items-center justify-between gap-4 flex-wrap`}
      data-testid={testid}
    >
      <div className="flex items-center gap-3 min-w-0">
        <Icon size={16} className="shrink-0" />
        <div className="min-w-0">{children[0]}</div>
      </div>
      <div className="shrink-0">{children[1]}</div>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}
